(() => {
  if (window.leslisAiLabels) return;
  window.leslisAiLabels = true;
  const records = new WeakMap();
  const known = new Map();
  const labels = new Map();
  const excluded = '.thumbnail-list, .thumbnail, .product__thumbnail, [data-leslis-ai-thumbnail]';
  const eligible = 'img[data-leslis-ai], img.pswp__img, .product-media-modal img';
  const annotatedSelector = 'img[data-leslis-ai], source[data-leslis-ai]';
  let trackedNodes = new Set();
  const label = document.querySelector('script[data-leslis-ai-label]')?.dataset.leslisAiLabel || 'AI';
  let candidates = [];
  let annotated = [];
  let discover = false;
  let recheck = false;
  let queued = false;
  let frame;

  function source(url) {
    if (!url) return '';
    try {
      const parsed = new URL(url, location.href);
      return parsed.pathname.split('/').slice(-2).join('/') + '?v=' + (parsed.searchParams.get('v') || '');
    } catch {
      return '';
    }
  }

  function record(node) {
    const value = node.dataset.leslisAi;
    const src = node.src;
    const srcset = node.srcset;
    const previous = records.get(node);
    if (previous && previous.value === value && previous.src === src && previous.srcset === srcset) return previous;
    const urls = (srcset || '').split(',').map((entry) => entry.trim().split(/\s+/)[0]);
    if (src) urls.push(src);
    const keys = new Set(urls.map(source).filter(Boolean));
    const result = { value, src, srcset, keys };
    records.set(node, result);
    keys.forEach((key) => known.set(key, value === '1'));
    return result;
  }

  function flag(img) {
    const key = source(img.currentSrc || img.src);
    if (!key || img.closest(excluded)) return false;
    const picture = img.parentElement?.tagName === 'PICTURE' ? img.parentElement : null;
    if (picture) {
      for (const node of picture.querySelectorAll('source[data-leslis-ai]')) {
        const entry = record(node);
        if ((!node.media || matchMedia(node.media).matches) && entry.keys.has(key)) return entry.value === '1';
      }
    }
    if (img.hasAttribute('data-leslis-ai')) {
      const entry = record(img);
      return entry.keys.has(key) && entry.value === '1';
    }
    return known.get(key) === true;
  }

  function position(img, badge, styles) {
    const styleFor = (node) => {
      if (!styles.has(node)) styles.set(node, getComputedStyle(node));
      return styles.get(node);
    };
    const parent = badge.parentElement;
    const bounds = img.getBoundingClientRect();
    // Off-screen images need no ancestor style or clipping measurements.
    if (!img.complete || !img.naturalWidth || bounds.width <= 0 || bounds.height <= 0 ||
      bounds.right <= 0 || bounds.bottom <= 0 || bounds.left >= window.innerWidth || bounds.top >= window.innerHeight) {
      if (!badge.hidden) badge.hidden = true;
      return;
    }
    const style = styleFor(img);
    const host = parent.getBoundingClientRect();
    let visible = img.complete && img.naturalWidth && bounds.width > 0 && bounds.height > 0 &&
      style.visibility !== 'hidden' && style.display !== 'none' && Number(style.opacity || 1) > 0;
    const viewport = {
      left: Math.max(0, bounds.left), top: Math.max(0, bounds.top),
      right: Math.min(window.innerWidth, bounds.right), bottom: Math.min(window.innerHeight, bounds.bottom)
    };
    // A Dawn lightbox is a scrolling host with several full-size images. A
    // badge belongs only to the visible part of its own reviewed image.
    for (let ancestor = parent; ancestor && visible; ancestor = ancestor.parentElement) {
      const ancestorStyle = styleFor(ancestor);
      visible = ancestorStyle.visibility !== 'hidden' && ancestorStyle.display !== 'none' && Number(ancestorStyle.opacity || 1) > 0;
      const clipX = /auto|scroll|hidden|clip/.test(ancestorStyle.overflowX || ancestorStyle.overflow);
      const clipY = /auto|scroll|hidden|clip/.test(ancestorStyle.overflowY || ancestorStyle.overflow);
      if (!clipX && !clipY) continue;
      const clip = ancestor.getBoundingClientRect();
      if (clipX) { viewport.left = Math.max(viewport.left, clip.left); viewport.right = Math.min(viewport.right, clip.right); }
      if (clipY) { viewport.top = Math.max(viewport.top, clip.top); viewport.bottom = Math.min(viewport.bottom, clip.bottom); }
    }
    // Category photos extend behind their caption. Keep the annotation out of
    // that covered area, including when only the bottom of a card is in view.
    const caption = img.closest('.leslis-categories__card')?.querySelector('.leslis-categories__copy');
    if (caption) {
      const captionBounds = caption.getBoundingClientRect();
      const fade = (parseFloat(styleFor(caption).getPropertyValue('--leslis-ai-caption-fade')) || 0) / 100;
      viewport.bottom = Math.min(viewport.bottom, captionBounds.top + captionBounds.height * fade);
    }
    const hidden = !visible || viewport.right - viewport.left < 29 || viewport.bottom - viewport.top < 29;
    if (badge.hidden !== hidden) badge.hidden = hidden;
    if (hidden) return;
    const scaleX = parent.offsetWidth ? host.width / parent.offsetWidth : 1;
    const scaleY = parent.offsetHeight ? host.height / parent.offsetHeight : 1;
    const left = (viewport.right - host.left - 29) / (scaleX || 1) + parent.scrollLeft - parent.clientLeft;
    const top = (viewport.bottom - host.top - 29) / (scaleY || 1) + parent.scrollTop - parent.clientTop;
    const value = `left:${left}px;top:${top}px;transform:scale(${1 / (scaleX || 1)},${1 / (scaleY || 1)})`;
    if (badge.getAttribute('style') !== value) badge.setAttribute('style', value);
  }

  function refresh() {
    queued = false;
    const styles = new Map();
    if (discover) {
      discover = false;
      const next = [...document.querySelectorAll(eligible)].filter((img) => !img.closest(excluded));
      const previous = new Set(candidates);
      const current = new Set(next);
      candidates.forEach((img) => { if (!current.has(img)) resize?.unobserve(img); });
      next.forEach((img) => { if (!previous.has(img)) resize?.observe(img); });
      candidates = next;
      annotated = [...document.querySelectorAll(annotatedSelector)];
      // Watch source metadata and image ancestors, not every animated element.
      attributes.disconnect();
      trackedNodes = new Set(annotated);
      candidates.forEach((img) => {
        for (let node = img; node; node = node.parentElement) trackedNodes.add(node);
      });
      trackedNodes.forEach((node) => attributes.observe(node, {
        attributes: true,
        attributeFilter: ['src', 'srcset', 'media', 'class', 'style', 'open', 'hidden']
      }));
      recheck = true;
    }
    if (recheck) {
      recheck = false;
      annotated.forEach((node) => { if (node.isConnected) record(node); });
      for (const [img, badge] of labels) {
        if (!img.isConnected || !img.matches(eligible) || !flag(img)) {
          badge.remove();
          labels.delete(img);
        }
      }
      candidates.forEach((img) => {
        if (!img.isConnected || !img.matches(eligible) || !flag(img) || !img.parentElement || img.closest('template')) return;
        const parent = img.parentElement.tagName === 'PICTURE' ? img.parentElement.parentElement : img.parentElement;
        if (!parent) return;
        if (labels.get(img)?.parentElement === parent) return;
        labels.get(img)?.remove();
        if (getComputedStyle(parent).position === 'static' || !getComputedStyle(parent).position) parent.classList.add('leslis-ai-host');
        const badge = document.createElement('span');
        badge.className = 'leslis-ai-label';
        badge.textContent = 'AI';
        badge.setAttribute('role', 'img');
        badge.setAttribute('aria-label', label);
        parent.append(badge);
        labels.set(img, badge);
      });
    }
    for (const [img, badge] of labels) {
      if (!img.isConnected) {
        badge.remove();
        labels.delete(img);
      } else {
        position(img, badge, styles);
      }
    }
  }

  function schedule(findImages = false, checkSources = false) {
    discover ||= findImages;
    recheck ||= checkSources;
    if (queued) return;
    queued = true;
    frame = requestAnimationFrame(refresh);
  }

  const resize = typeof ResizeObserver === 'function' ? new ResizeObserver(() => schedule(false, true)) : null;
  const attributes = new MutationObserver((mutations) => {
    const checkSources = mutations.some((change) =>
      change.attributeName === 'class' || change.target.matches('img, source, picture'));
    schedule(false, checkSources);
  });
  const observer = new MutationObserver((mutations) => {
    let layout = false;
    for (const change of mutations) {
      if (change.type === 'attributes') {
        // Newly reviewed images can become eligible without being reinserted.
        discover = true;
        layout = true;
        continue;
      }
      const nodes = [...change.addedNodes, ...change.removedNodes].filter((node) =>
        node.nodeType === 1 && !node.classList.contains('leslis-ai-label'));
      if (!nodes.length) continue;
      if (nodes.some((node) => node.matches('img, source') || node.querySelector('img, source'))) {
        discover = true;
        layout = true;
      } else if (trackedNodes.has(change.target)) {
        layout = true;
      }
    }
    if (layout) schedule();
  });
  observer.observe(document.documentElement, {
    subtree: true, childList: true, attributes: true, attributeFilter: ['data-leslis-ai']
  });
  document.addEventListener('load', (event) => {
    if (event.target.matches?.(eligible)) schedule(false, true);
  }, true);
  document.addEventListener('transitionend', (event) => {
    if (trackedNodes.has(event.target)) schedule();
  });
  document.addEventListener('scroll', () => {
    if (labels.size) schedule();
  }, { capture: true, passive: true });
  document.addEventListener('shopify:section:load', () => schedule(true, true));
  window.addEventListener('resize', () => schedule(false, true), { passive: true });
  window.addEventListener('pagehide', (event) => {
    if (event.persisted) return;
    observer.disconnect();
    attributes.disconnect();
    resize?.disconnect?.();
    cancelAnimationFrame(frame);
  });
  schedule(true, true);
})();
